import { useEffect, useRef } from "react";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SOAP_KEYS } from "@/lib/soap";
import { copyText } from "@/lib/clipboard";

const SEG_STYLE = {
  plain: "",
  old: "rounded bg-red-100 px-0.5 text-red-700 line-through decoration-red-500",
  new: "rounded bg-emerald-100 px-0.5 text-emerald-900 ring-1 ring-emerald-300",
};

function FixPreview({ k, segs, focus }) {
  const ref = useRef(null);
  useEffect(() => { if (focus) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [focus, segs]);
  let first = true;
  return (
    <div className="min-h-[120px] px-3 py-2 text-[15px] leading-relaxed whitespace-pre-wrap text-slate-700" data-testid={`soap-${k}-preview`}>
      {segs.map((s, i) => {
        const isFirst = s.type !== "plain" && first;
        if (isFirst) first = false;
        const Tag = s.type === "plain" ? "span" : s.type === "old" ? "del" : "ins";
        return <Tag key={i} ref={isFirst ? ref : undefined} className={`${SEG_STYLE[s.type]} ${s.type === "new" ? "no-underline" : ""}`}
          data-testid={s.type === "plain" ? undefined : `fix-preview-${s.type}`}>{s.text}</Tag>;
      })}
    </div>
  );
}

export function SoapSections({ values, onChange, streaming, preview }) {
  const focus = SOAP_KEYS.find((s) => preview?.[s.key])?.key;
  return (
    <div className="space-y-4">
      {SOAP_KEYS.map((s) => (
        <div key={s.key} className={`rounded-xl border bg-white overflow-hidden focus-within:border-primary/50 transition-colors ${preview?.[s.key] ? "border-amber-400 ring-2 ring-amber-200" : ""}`}>
          <div className="flex items-center gap-3 border-b bg-secondary/50 px-4 py-2.5">
            <span className="h-7 w-7 rounded-md bg-primary text-white grid place-items-center font-display font-bold text-sm">{s.letter}</span>
          <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">{s.label}</div>
              <div className="text-[11px] text-slate-500">{s.hint}</div>
            </div>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-slate-500 hover:text-primary" disabled={streaming || !values[s.key]}
              onClick={() => copyText(values[s.key], `${s.label} copied`)} data-testid={`soap-${s.key}-copy-button`}>
              <Copy className="h-3.5 w-3.5 mr-1" />Copy
            </Button>
          </div>
          {preview?.[s.key] ? <FixPreview k={s.key} segs={preview[s.key]} focus={focus === s.key} /> : <Textarea
            value={values[s.key] || ""}
            onChange={(e) => onChange(s.key, e.target.value)}
            readOnly={streaming}
            placeholder={streaming ? "Writing…" : `${s.label} will appear here — or type it yourself.`}
            className="min-h-[120px] border-0 rounded-none focus-visible:ring-0 text-[15px] leading-relaxed resize-y"
            data-testid={`soap-${s.key}-textarea`}
          />}
        </div>
      ))}
    </div>
  );
}
